import './input.css';
import { LexeekConnector } from './connector';
import { MarkdownRenderer } from './renderer';
import { SidebarManager } from './SidebarComponent';
import { PreviewComponent } from './PreviewComponent';


class LexeekSearch {
    constructor(containerId, config) {
        this.containerId = containerId;
        this.config = {
            endpoint: config.endpoint || 'http://127.0.0.1:8888',
            apiKey: config.apiKey
        };

        this.state = {
            currentAnswer: '',
            isModalOpen: false,
            initialized: false,
            streamStatus: null,
            streamLoading: false,
            currentPreview: null,
            isDarkMode: this.lxkGetInitialTheme(config.defaultTheme)
        };

        this.container = null;
        this.previewComponent = null;
        this.eventListeners = new Set();

        // Core UI element IDs
        this.widgetButtonId = "lxk-widget-button";
        this.modalDivId = "lxk-search-modal";
        this.modalTitleId = "lxk-modal-title";
        this.modalContentId = "lxk-modal-content";
        this.modalCloseButtonId = "lxk-modal-close";
        this.searchInputId = "lxk-search-input";
        this.askButtonId = "lxk-ask-button";
        this.mobileAskButtonId = "lxk-mobile-ask-button";

        this.sidebarId = "lxk-sidebar";
        this.sidebarTitleId = "lxk-sidebar-title";
        this.darkModeToggleId = "lxk-theme-toggle";

        this.searchDebounceTimeout = null;
        this.searchDebounceDelay = 300;
        
        this.markdownRenderer = new MarkdownRenderer();
        this.connector = new LexeekConnector(this.config);
        this.sidebarManager = new SidebarManager((source) => {
            if (source.redirect_url) {
                window.open(source.redirect_url, '_blank');
            }
        });
    }

    async lxkInitialize() {
        if (this.state.initialized) return;
            
        this.container = document.getElementById(this.containerId);
        if (!this.container) {
            throw new Error(`Container with id '${this.containerId}' not found`);
        }
        this.container.className = "lexeek-wrapper";
        
        // Initialize styles and HTML
        await this.lxkInjectStyles();
        this.container.innerHTML = this.lxkCreateWidgetHTML();
            
        // Setup event listeners
        const chatButton = this.container.querySelector(`#${this.widgetButtonId}`);
        const modalClose = this.container.querySelector(`#${this.modalCloseButtonId}`);
        const themeToggle = this.container.querySelector(`#${this.darkModeToggleId}`);
            
        chatButton.addEventListener('click', this.lxkToggleModal);
        modalClose.addEventListener('click', this.lxkToggleModal);

        this.eventListeners.add(chatButton);
        this.eventListeners.add(modalClose);

        if (themeToggle) {
            themeToggle.addEventListener('click', this.lxkToggleTheme);
            this.eventListeners.add(themeToggle);
        }
            
        // Set initial theme
        const modal = this.container.querySelector(`#${this.modalDivId}`);
        if (modal && this.state.isDarkMode) {
            modal.classList.add('lexeek-dark');
        }

        const searchInput = this.container.querySelector(`#${this.searchInputId}`);
        if (searchInput) {
            const debouncedSearch = this.lxkDebounce(
                (value) => this.lxkHandleSearch(value),
                this.searchDebounceDelay
            );

            searchInput.addEventListener('input', (e) => {
                debouncedSearch(e.target.value);
            });
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.lxkHandleAskAI();
                }
            });
            
            this.eventListeners.add(searchInput);
        }

        this.previewComponent = new PreviewComponent();
        const askButton = this.container.querySelector(`#${this.askButtonId}`);
        const mobileAskButton = this.container.querySelector(`#${this.mobileAskButtonId}`);
        if (askButton) {
            askButton.addEventListener('click', () => this.lxkHandleAskAI());
            this.eventListeners.add(askButton);
        }

        if (mobileAskButton) {
            mobileAskButton.addEventListener('click', () => this.lxkHandleAskAI());
            this.eventListeners.add(mobileAskButton);
        }

        // Show initial samples
        this.lxkShowInitialSamples();
        
        this.state.initialized = true;
    }

    async lxkInjectStyles() {
        try {
            const { css, messages } = await this.connector.initialize();
                
            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-lexeek', 'true');
            styleElement.textContent = css;
            document.head.appendChild(styleElement);

            // If we have cached messages, render them

        } catch (error) {
            console.error('Error loading styles:', error);
        }
    }

    lxkGetInitialTheme(defaultTheme) {
        try {
            // If a default theme is provided in config, use it
            if (defaultTheme === 'dark' || defaultTheme === 'light') {
                return defaultTheme === 'dark';
            }

            // Try to detect system preference if available
            if (typeof window !== 'undefined' && window.matchMedia) {
                return window.matchMedia('(prefers-color-scheme: dark)').matches;
            }

            // Fallback to light theme
            return false;
        } catch (error) {
            console.warn('Error detecting theme preference:', error);
            return false; // Fallback to light theme
        }
    }

    lxkGetThemeIcon() {
        // Sun icon for dark mode (indicating switch to light)
        const sunIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lxk-w-5 lxk-h-5 md:lxk-w-6 md:lxk-h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
        </svg>`;

        // Moon icon for light mode (indicating switch to dark)
        const moonIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lxk-w-5 lxk-h-5 md:lxk-w-6 md:lxk-h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
        </svg>`;

        return this.state.isDarkMode ? sunIcon : moonIcon;
    }

    lxkToggleTheme = () => {
        try {
            this.state.isDarkMode = !this.state.isDarkMode;
            const modal = this.container.querySelector(`#${this.modalDivId}`);
            const themeButton = this.container.querySelector(`#${this.darkModeToggleId}`);
            
            if (modal && themeButton) {
                if (this.state.isDarkMode) {
                    modal.classList.add('lexeek-dark');
                } else {
                    modal.classList.remove('lexeek-dark');
                }
                
                themeButton.innerHTML = this.lxkGetThemeIcon();
            }

            // If config provides a theme change callback, call it
            if (this.config.onThemeChange && typeof this.config.onThemeChange === 'function') {
                this.config.onThemeChange(this.state.isDarkMode ? 'dark' : 'light');
            }
        } catch (error) {
            console.error('Error toggling theme:', error);
        }
    }

    lxkToggleModal = () => {
        this.state.isModalOpen = !this.state.isModalOpen;
        const modal = this.container.querySelector(`#${this.modalDivId}`);

        if (this.state.isModalOpen) {
            modal.classList.remove('lxk-hidden');
            document.body.style.overflow = 'hidden';
        } else {
            modal.classList.add('lxk-hidden');
            document.body.style.overflow = 'auto';
        }
    }

    async lxkRenderMarkdown(markdown, selector) {
        const targetElement = this.container.querySelector(selector);
        if (!targetElement) {
            throw new Error(`Element with selector '${selector}' not found within container`);
        }

        const newEventListeners = await this.markdownRenderer.render(markdown, targetElement);
        newEventListeners.forEach(listener => this.eventListeners.add(listener));
    }

    lxkGetBackground() {
        const computedStyle = getComputedStyle(document.documentElement);
        const gradientA = computedStyle.getPropertyValue('--lexeek-surface-gradient-a').trim();
        const gradientB = computedStyle.getPropertyValue('--lexeek-surface-gradient-b').trim();
        const direction = computedStyle.getPropertyValue('--lexeek-surface-gradient-direction').trim();

        if (gradientA === '#00000000' || gradientB === '#00000000') {
            return '';
        }

        const directionMap = {
            'to top': 'lxk-bg-gradient-to-t',
            'to top left': 'lxk-bg-gradient-to-tl',
            'to top right': 'lxk-bg-gradient-to-tr',
            'to top bottom': 'lxk-bg-gradient-to-tb',

            'to bottom': 'lxk-bg-gradient-to-b',
            'to bottom left': 'lxk-bg-gradient-to-bl',
            'to bottom right': 'lxk-bg-gradient-to-br',
            'to bottom top': 'lxk-bg-gradient-to-bt',

            'to right': 'lxk-bg-gradient-to-r',
            'to right left': 'lxk-bg-gradient-to-rl',
            'to right top': 'lxk-bg-gradient-to-rt',
            'to right bottom': 'lxk-bg-gradient-to-rb',

            'to left': 'lxk-bg-gradient-to-l',
            'to left top': 'lxk-bg-gradient-to-lt',
            'to left right': 'lxk-bg-gradient-to-lr',
            'to left bottom': 'lxk-bg-gradient-to-lb',
        };

        const classes = [];
        const gradientDirection = directionMap[direction] || 'lxk-bg-gradient-to-r';

        classes.push(gradientDirection);
        classes.push(`lxk-from-lexeek-gradient-shade-a`);
        classes.push(`lxk-to-lexeek-gradient-shade-b`);

        return classes.join(' ');
    }

    lxkCreateWidgetHTML() {
        const gradientStyle = this.lxkGetBackground();
        return `
            <button id="${this.widgetButtonId}" class="lxk-fixed lxk-bottom-4 lxk-left-4 md:lxk-bottom-6 md:lxk-left-6 lxk-bg-lexeek-widget-bg lxk-text-lexeek-widget-text hover:lxk-bg-lexeek-widget-bg-hover lxk-p-3 md:lxk-p-4 lxk-rounded-full lxk-shadow-lg hover:lxk-shadown-xl lxk-transition lxk-duration-300 lxk-flex lxk-items-center lxk-border-none lxk-appearance-none" style="z-index: 10000;">
                <svg class="lxk-w-5 lxk-h-5 md:lxk-w-6 md:lxk-h-6 lxk-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                </svg>
                <span class="lxk-text-lg lxk-font-semibold">Ask Lex</span>
            </button>
    
            <div id="${this.modalDivId}" class="lxk-hidden lxk-fixed lxk-inset-0 lxk-backdrop-blur-sm lxk-bg-lexeek-modal-blur lxk-flex lxk-items-center lxk-justify-center lxk-overflow-hidden" style="z-index: 10001;">
                <!-- Modal Container -->
                <div class="lxk-w-full lxk-h-[100vh] md:lxk-h-[90vh] md:lxk-max-w-[90vh] lg:lxk-max-w-[1400px] lxk-bg-lexeek-surface lxk-rounded-none md:lxk-rounded-xl lxk-drop-shadow-xl lxk-flex lxk-flex-col lxk-relative ${gradientStyle} lxk-overflow-hidden">
                    
                    <!-- Modal Header -->
                    <div class="lxk-px-4 lxk-py-4 lxk-flex lxk-justify-between lxk-items-center lxk-border-b lxk-border-lexeek-accent/10">
                        <!-- Left side with titles -->
                        <div class="lxk-flex lxk-items-center">
                            <!-- Sidebar Title Section -->
                            <div id="${this.sidebarTitleId}" class="lxk-hidden lxk-ml-8 md:lxk-flex lxk-items-center lxk-w-[250px] lg:lxk-w-[300px]">
                                <h2 class="lxk-text-2xl lxk-font-semibold lxk-text-lexeek-content">
                                Recent Searches
                                </h2>
                            </div>
                            
                            <!-- Main Content Title -->
                            <div class="lxk-flex lxk-items-center">
                                <h2 id="${this.modalTitleId}" class="lxk-ml-6 lxk-text-2xl lxk-font-semibold lxk-text-lexeek-content">
                                    What is lexeek?
                                </h2>
                            </div>
                        </div>

                        <!-- Right side with buttons -->
                        <div class="lxk-flex lxk-items-center lxk-gap-3">
                            <!-- Dark Mode Toggle Button -->
                            <button id="${this.darkModeToggleId}" class="lxk-text-lexeek-content-alt hover:lxk-text-lexeek-accent lxk-transition-colors lxk-bg-transparent lxk-border-none lxk-p-2 lxk-rounded-full hover:lxk-bg-lexeek-accent/10 lxk-appearance-none">
                                ${this.lxkGetThemeIcon()}
                            </button>
                            <!-- Close Button -->
                            <button id="${this.modalCloseButtonId}" class="lxk-text-lexeek-content-alt hover:lxk-text-lexeek-accent lxk-transition-colors lxk-bg-transparent lxk-border-none lxk-p-2 lxk-rounded-full hover:lxk-bg-lexeek-accent/10 lxk-appearance-none">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="lxk-w-5 lxk-h-5 md:lxk-w-6 md:lxk-h-6">
                                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                </svg>
                            </button>
                        </div>
                    </div>
    
                    <!-- Modal Content Area -->
                    <div class="lxk-flex lxk-flex-1 lxk-overflow-hidden">
                        <!-- Sidebar - Hidden on mobile -->
                        <div id="${this.sidebarId}" class="lxk-hidden md:lxk-block md:lxk-w-[250px] lg:lxk-w-[300px] lxk-overflow-y-auto">
                            <!-- Sidebar content will be injected here -->
                        </div>
    
                        <!-- Main Content Area -->
                        <div id="${this.modalContentId}" class="lxk-flex-1 lxk-overflow-y-auto lxk-markdown-content lxk-px-8 lxk-py-4">
                            <!-- Main content will be injected here -->
                        </div>
                    </div>
    
                    <!-- Search Input Container -->
                    <div class="lxk-px-2 lxk-pb-2 lxk-mt-auto lxk-mx-auto lxk-w-[calc(100%-2rem)] md:lxk-w-[640px] lg:lxk-w-[768px] lxk-mb-11">
                        <div class="lxk-relative lxk-flex lxk-items-center lxk-max-w-full">
                            <div class="lxk-absolute lxk-left-4">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="lxk-w-5 lxk-h-5 lxk-text-lexeek-content-alt">
                                    <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <input 
                                id="${this.searchInputId}"
                                type="text" 
                                placeholder="Ask about engagespot..." 
                                class="lxk-w-full lxk-pl-12 lxk-pr-16 md:lxk-pr-[110px] lxk-py-4 md:lxk-py-3 
                                    lxk-shadow-none lxk-appearance-none lxk-rounded-full 
                                    lxk-text-lexeek-content lxk-bg-lexeek-surface lxk-text-base 
                                    lxk-outline-none lxk-border-none
                                    lxk-ring-1 lxk-ring-lexeek-accent
                                    focus:lxk-ring-2 focus:lxk-ring-lexeek-accent-hover
                                    lxk-ring-offset-2 lxk-ring-offset-lexeek-surface
                                    focus:lxk-ring-offset-2 focus:lxk-ring-offset-lexeek-surface"
                            />
                            <!-- Mobile Button -->
                            <button id="${this.mobileAskButtonId}" class="lxk-absolute lxk-top-1/2 lxk-transform -lxk-translate-y-1/2 lxk-right-1 md:lxk-hidden lxk-h-12 lxk-w-12 lxk-rounded-full lxk-bg-lexeek-widget-bg lxk-text-lexeek-widget-text lxk-flex lxk-items-center lxk-justify-center lxk-border-none lxk-appearance-none lxk-outline-none focus:lxk-outline-none">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="lxk-w-5 lxk-h-5">
                                    <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
                                </svg>
                            </button>
    
                            <!-- Desktop Button -->
                            <button id="${this.askButtonId}" class="lxk-hidden md:lxk-flex lxk-absolute lxk-top-1/2 lxk-transform -lxk-translate-y-1/2 lxk-right-0.5 lxk-h-11 lxk-px-4 lxk-rounded-full lxk-bg-lexeek-widget-bg lxk-text-lexeek-widget-text lxk-items-center lxk-gap-1 lxk-border-none lxk-appearance-none lxk-outline-none focus:lxk-outline-none">
                                <span>Ask Lex</span>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="lxk-w-5 lxk-h-5">
                                    <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
                                </svg>
                            </button>
                        </div>
                    </div>
    
                    <!-- Powered by Lexeek -->
                    <div class="lxk-block lxk-absolute lxk-bottom-4 lxk-right-4 lxk-shadow-md">
                        <span class="lxk-text-sm lxk-text-lexeek-content-alt lxk-bg-lexeek-surface-alt lxk-px-3 lxk-py-1 lxk-rounded-md lxk-border lxk-border-lexeek-accent">
                            Powered by Lexeek
                        </span>
                    </div>
                </div>
            </div>
        `;
    }

    lxkSetUILoading(loading) {
        const searchInput = this.container.querySelector(`#${this.searchInputId}`);
        const askButton = this.container.querySelector(`#${this.askButtonId}`);
        const mobileAskButton = this.container.querySelector(`#${this.mobileAskButtonId}`);
        
        if (loading) {
            searchInput?.setAttribute('disabled', 'disabled');
            askButton?.setAttribute('disabled', 'disabled');
            mobileAskButton?.setAttribute('disabled', 'disabled');
            
            // Optional: Add loading styles
            askButton?.classList.add('lxk-opacity-50', 'lxk-cursor-not-allowed');
            mobileAskButton?.classList.add('lxk-opacity-50', 'lxk-cursor-not-allowed');
            searchInput?.classList.add('lxk-opacity-50', 'lxk-cursor-not-allowed');
        } else {
            searchInput?.removeAttribute('disabled');
            askButton?.removeAttribute('disabled');
            mobileAskButton?.removeAttribute('disabled');
            
            // Remove loading styles
            askButton?.classList.remove('lxk-opacity-50', 'lxk-cursor-not-allowed');
            mobileAskButton?.classList.remove('lxk-opacity-50', 'lxk-cursor-not-allowed');
            searchInput?.classList.remove('lxk-opacity-50', 'lxk-cursor-not-allowed');
        }
    }

    // In LexeekSearch class, add this new method
    lxkShowInitialSamples() {
        // Get the elements
        const sidebar = this.container.querySelector(`#${this.sidebarId}`);
        const modalTitle = this.container.querySelector(`#${this.modalTitleId}`);
        const searchInput = this.container.querySelector(`#${this.searchInputId}`);
        const modalContent = this.container.querySelector(`#${this.modalContentId}`);
        const sidebarTitle = this.container.querySelector(`#${this.sidebarTitleId} h2`);
    
        // Update titles
        if (sidebarTitle) {
            sidebarTitle.textContent = "Recent Searches";
        }
        if (modalTitle) {
            modalTitle.textContent = "Getting Started with Engagespot";
        }
    
        // Show "No searches yet" in sidebar
        if (sidebar) {
            sidebar.innerHTML = `
                <div class="lxk-flex lxk-flex-col lxk-items-center lxk-justify-center lxk-h-full lxk-p-4">
                    <p class="lxk-text-lexeek-content-alt lxk-text-center">No searches yet</p>
                </div>
            `;
        }
    
        // Create content with clickable suggestions
        if (modalContent) {
            const suggestions = [
                "What is engagespot?",
                "How to use engagespot with slack notifications",
                "Express.js and twilio integration with engagespot",
                "SMS fall back configuration while integrating with engagespot"
            ];
    
            modalContent.innerHTML = `
                <div class="lxk-w-full">
                    <div class="lxk-p-4">
                        <h2 class="lxk-text-2xl lxk-font-semibold lxk-text-lexeek-content lxk-mb-6">Search anything about Engagespot</h2>
                        
                        <!-- Search Suggestions -->
                        <div class="lxk-space-y-3 lxk-w-full lxk-text-lg">
                            ${suggestions.map(suggestion => `
                                <div class="lxk-max-w-lg lxk-px-4 lxk-py-2.5 lxk-bg-lexeek-surface-alt lxk-border lxk-border-lexeek-surface-alt lxk-rounded-lg lxk-text-lexeek-content hover:lxk-border-accent lxk-cursor-pointer lxk-transition-all" data-suggestion="${suggestion}">
                                    ${suggestion}
                                </div>
                            `).join('')}
                        </div>
                    </div>
                </div>
            `;
    
            // Add click handlers for suggestions
            const suggestionElements = modalContent.querySelectorAll('[data-suggestion]');
            suggestionElements.forEach(element => {
                element.addEventListener('click', () => {
                    if (searchInput) {
                        searchInput.value = element.dataset.suggestion;
                        searchInput.focus();
                    }
                });
                this.eventListeners.add(element);
            });
        }
    }


    /* Handle debounce sreach */
    lxkDebounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    async lxkHandleSearch(query) {
        if (!query.trim()) {
            const sidebar = this.container.querySelector(`#${this.sidebarId}`);
            if (sidebar) {
                sidebar.innerHTML = '';
            }
            return;
        }

        try {
            const response = await this.connector.search(query);
            const data = await response.json();
            
            const titleElement = this.container.querySelector(`#${this.sidebarTitleId} h2`);
            if (titleElement) {
                titleElement.textContent = "Search Results";
            }

            const sidebar = this.container.querySelector(`#${this.sidebarId}`);
            if (sidebar) {
                sidebar.innerHTML = this.sidebarManager.lxkInjectToSideBar(data);
            }
            if (data && data.sources && data.sources.length > 0) {
                this.PreviewComponent.showSourcePreview(data.sources);
            }
        } catch (error) {
            console.error('Search error:', error);
        }
    }

    /* Handle ask AI */
    async lxkUpdatePreview(source) {
        const modalContent = this.container.querySelector(`#${this.modalContentId}`);
        const modalTitle = this.container.querySelector(`#${this.modalTitleId}`);
     
        if (modalContent && modalTitle) {
            try {
                this.state.currentPreview = source;
                modalTitle.textContent = source?.title || 'Preview';
                await this.previewComponent.render(modalContent, this.state);
            } catch (error) {
                console.error('Preview update error:', error);
            }
        }
    }

    async lxkHandleAskAI() {
        const searchInput = this.container.querySelector(`#${this.searchInputId}`);
        const modalContent = this.container.querySelector(`#${this.modalContentId}`);
        const modalTitle = this.container.querySelector(`#${this.modalTitleId}`);
        
        const query = searchInput?.value?.trim();
        if (!query) return;
    
        try {
            // Set loading state
            this.state.streamLoading = true;
            this.lxkSetUILoading(true);
    
            this.state.currentPreview = null;
            this.previewComponent.resetStream();
            modalTitle.textContent = query;
    
            const response = await this.connector.startConversation(query);
            this.connector.handleStreamResponse(
                response,
                async (data) => {
                    const newEventListeners = await this.previewComponent.handleStreamData(
                        {...data, question: query}, 
                        modalContent
                    );
    
                    if (data.sources?.length > 0) {
                        const sidebar = this.container.querySelector(`#${this.sidebarId}`);
                        if (sidebar) {
                            sidebar.innerHTML = this.sidebarManager.lxkInjectToSideBar({sources: data.sources});
                        }
                    }
    
                    if (newEventListeners) {
                        newEventListeners.forEach(listener => this.eventListeners.add(listener));
                    }
                },
                (error) => {
                    console.error('Stream error:', error);
                    modalContent.innerHTML = this.previewComponent.getEmptyState();
                    // Reset loading state on error
                    this.state.streamLoading = false;
                    this.lxkSetUILoading(false);
                },
                () => {
                    searchInput.value = '';
                    // Reset loading state on completion
                    this.state.streamLoading = false;
                    this.lxkSetUILoading(false);
                }
            );
        } catch (error) {
            console.error('Ask AI error:', error);
            modalContent.innerHTML = this.previewComponent.getEmptyState();
            // Reset loading state on error
            this.state.streamLoading = false;
            this.lxkSetUILoading(false);
        }
    }

    async lxkHandleSearch(query) {
        if (!query.trim()) {
            const sidebar = this.container.querySelector(`#${this.sidebarId}`);
            if (sidebar) {
                sidebar.innerHTML = '';
            }
            // Reset preview if search is empty
            this.lxkUpdatePreview(null);
            return;
        }

        try {
            const response = await this.connector.search(query);
            const data = await response.json();
            
            const titleElement = this.container.querySelector(`#${this.sidebarTitleId} h2`);
            if (titleElement) {
                titleElement.textContent = "Search Results";
            }

            const sidebar = this.container.querySelector(`#${this.sidebarId}`);
            if (sidebar) {
                const content = this.sidebarManager.lxkInjectToSideBar(data);
                sidebar.innerHTML = content;

                // Add click handlers for preview
                const sourceElements = sidebar.querySelectorAll('[data-redirect-url]');
                sourceElements.forEach(element => {
                    const source = data.sources.find(s => s.redirect_url === element.dataset.redirectUrl);
                    if (source) {
                        element.addEventListener('click', () => this.lxkUpdatePreview(source));
                        this.eventListeners.add(element);
                    }
                });
            }
        } catch (error) {
            console.error('Search error:', error);
        }
    }


    /*===================================*/
    /* Component Cleanup and Destruction */
    /*===================================*/
    lxkCleanup() {
        try {
            // Cleanup preview component
            if (this.previewComponent) {
                this.previewComponent.cleanup();
            }
            
            // Remove styles
            document.querySelectorAll('style[data-lexeek]').forEach(el => el.remove());
    
            if (this.container) {
                // Clean up event listeners
                this.eventListeners.forEach(element => {
                    if (element && element.removeEventListener) {
                        const clone = element.cloneNode(true);
                        element.parentNode.replaceChild(clone, element);
                    }
                });
    
                // Clean up enhanced UI elements
                ['lxk-code-block-wrapper', 'lxk-image-wrapper', 'lxk-table-wrapper'].forEach(className => {
                    this.container.querySelectorAll(`.${className}`).forEach(wrapper => {
                        const content = wrapper.querySelector('pre, img, table');
                        if (content && wrapper.parentNode) {
                            wrapper.parentNode.insertBefore(content, wrapper);
                            wrapper.remove();
                        }
                    });
                });
    
                // Remove modal if open
                const modalElement = this.container.querySelector(`#${this.modalDivId}`);
                if (modalElement) {
                    modalElement.remove();
                }
    
                // Restore body scroll if needed
                if (this.state.isModalOpen) {
                    document.body.style.overflow = 'auto';
                }
    
                // Clear container
                this.container.innerHTML = '';
            }
    
            // Reset state
            this.state = {
                currentAnswer: '',
                isModalOpen: false,
                initialized: false
            };
    
            // Clear event listeners
            this.eventListeners.clear();
    
        } catch (error) {
            console.error('Error during cleanup:', error);
            throw new Error('Failed to properly cleanup LexeekSearch instance');
        }
    }

    lxkDestroy() {
        try {
            // Run cleanup first
            this.lxkCleanup();

            // Clear instance references
            this.container = null;
            this.config = null;
            this.state = null;
            this.markdownRenderer = null;
            this.connector = null;
            this.eventListeners = null;
            this.sidebarManager = null;
            this.searchDebounceTimeout = null;

            // Clear all IDs
            [
                'widgetButtonId', 
                'modalDivId', 
                'modalTitleId', 
                'modalContentId', 
                'modalCloseButtonId',
                'searchInputId', 
                'askButtonId', 
                'mobileAskButtonId'
            ].forEach(id => {
                this[id] = null;
            });

        } catch (error) {
            console.error('Error during destroy:', error);
            throw new Error('Failed to properly destroy LexeekSearch instance');
        }
    }
}

// Export the class for ES modules
export default LexeekSearch;